/**
 * Button — headless proprietary primitive.
 *
 * Client component (consumes `PermissionContext` + `AuditContext` via
 * `useContext`). See `docs/adr/primitives/001-button-rsc-default.md` for
 * the full trade-off analysis.
 *
 * Contracts honored:
 *   - Permission gate (ARCHITECTURE §10). The button asks the ambient
 *     policy `can('click', 'button', attrs?)`. Denied → the `mode` field
 *     decides presentation (`'disabled'`, `'hidden'`, or `'readOnly'`).
 *   - Audit event (§10). Every successful activation emits
 *     `PRIMITIVE_ACTIONS.BUTTON_CLICK` with ISO-8601 timestamp. Denied
 *     activations emit `DATA_ACTIONS.PERMISSION_DENIED` with the deny
 *     reason so governance can index on them.
 */

'use client';

import { forwardRef } from 'react';
import type {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'disabled' | 'aria-busy'
>;

export interface ButtonProps extends NativeButtonProps {
  /**
   * Button contents. Unstyled — wrap with icons / labels as needed. The
   * visual layer in `@oshon-ai/components` adds spacing and iconography
   * on top of this primitive.
   */
  children: ReactNode;
  /**
   * Native `type` attribute. Defaults to `'button'` so the primitive
   * never accidentally submits a form. Pass `'submit'` explicitly for
   * form buttons.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Disable the button. Renders with `aria-disabled="true"` and the
   * native `disabled` attribute so screen readers and keyboard users
   * get consistent signal. When `disabled`, `onClick` does not fire and
   * no audit event is emitted.
   */
  disabled?: boolean;
  /**
   * In-flight state. Sets `aria-busy="true"` + `disabled` so assistive
   * tech announces the button as unavailable while the action resolves.
   * The visual layer renders a spinner on top; the primitive itself does
   * not.
   */
  loading?: boolean;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. Example: `permissions={{ mode: 'hidden' }}`
   * switches this one instance to render-as-null when denied.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name passed to `permissions.can(action, resource, attrs)`.
   * Default `'button'`. Consumers override for specific semantics, e.g.
   * `resource="row:edit"` for a row-level action button.
   */
  resource?: string;
  /**
   * Attribute bag forwarded to `permissions.can(..., attrs)` for
   * attribute-based access control (row ID, tenant, owner). Primitives
   * never invent values here — the consumer supplies them.
   */
  permissionAttrs?: Record<string, unknown>;
}

/**
 * Headless button. Accessible `<button>` with:
 *   - `type="button"` default (no accidental form submits)
 *   - `disabled` + `aria-disabled` + `aria-busy` synchronised
 *   - Permission gating via `can('click', resource, attrs)` with `mode`
 *     controlling the denied-state presentation
 *   - Canonical `button:click` audit event on activation; `permission:denied`
 *     with deny reason on blocked activation
 *   - `data-oshon-primitive="button"` styling hook for the components layer
 *   - `data-permission-state="denied"` hook when mode is `'disabled'` or
 *     `'readOnly'` and the policy denied the action
 *   - No visual styling — consumers supply their own className / styles.
 *
 * Keyboard: native browser behavior — Enter + Space activate.
 *
 * @see docs/adr/primitives/001-button-rsc-default.md
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      type = 'button',
      disabled = false,
      loading = false,
      permissions,
      resource = 'button',
      permissionAttrs,
      onClick,
      ...rest
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) {
    const contextPermissions = usePermissions();
    const effective = resolvePermissions(permissions, contextPermissions);
    const mode = effective.mode ?? 'disabled';
    const emit = useAudit();

    const { granted, denyReason } = evaluatePermission(
      effective,
      'click',
      resource,
      permissionAttrs,
    );

    // Permission denied + mode='hidden' → the affordance must not exist.
    if (!granted && mode === 'hidden') return null;

    // `readOnly` denies the click but keeps the affordance visible +
    // focusable. `disabled` denies + visibly disables. Both emit the
    // permission:denied audit trail on interaction attempts.
    const permissionBlocks = !granted && mode !== 'readOnly';
    const isDisabled = disabled || loading || permissionBlocks;

    const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
      // Local disabled/loading — silent drop, no audit.
      if (disabled || loading) return;

      if (!granted) {
        // Record the denial regardless of `mode`. Governance wants these
        // events to surface over-permissive UIs as much as under-permissive
        // ones.
        emit({
          component: 'button',
          action: CANONICAL_ACTIONS.PERMISSION_DENIED,
          permissionResult: 'denied',
          ...(denyReason ? { denyReason } : {}),
          details: { attemptedAction: 'click', resource },
        });
        // `readOnly` means "visible, non-mutating" — we don't invoke
        // onClick but we also don't `preventDefault` the browser's click
        // (it has no meaningful default behavior on a plain button).
        return;
      }

      emit({
        component: 'button',
        action: CANONICAL_ACTIONS.BUTTON_CLICK,
        permissionResult: 'granted',
      });
      onClick?.(event);
    };

    const permissionDataState =
      !granted && mode !== 'hidden' ? 'denied' : undefined;

    // Consumer title always wins; deny reason fills in only when no title.
    const resolvedTitle =
      rest.title ?? (permissionDataState && denyReason ? denyReason : undefined);

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        data-oshon-primitive="button"
        data-loading={loading ? 'true' : undefined}
        data-permission-state={permissionDataState}
        title={resolvedTitle}
        onClick={handleClick}
      >
        {children}
      </button>
    );
  },
);
